import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import type TASProgramTemplateModel from 'tio-common/models/tas-program-template';
import type EmployeeModel from 'tio-common/models/employee';
import { filterEligibleTemplates } from 'tio-common/utils/tuition-assistance/filter-eligibile-templates';
import type FeaturesService from 'tio-common/services/features';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { executeSearchRequest } from 'tio-common/requests/search';
import type { OpenSearchResponse } from 'tio-common/models/search';
import {
  type RecentApplication,
  type ProgramInstance,
  ALL_APPLICATION_STATES,
  ALL_PROGRAM_INSTANCE_STATES,
  ACTIVE_APPLICATION_STATES,
  ACTIVE_INSTANCE_STATES,
  isActiveApplicationState,
  isActiveInstanceState,
  isInRepayment,
  normalizePaymentFields,
} from 'tio-common/utils/tuition-assistance/application-states';

export default class AuthenticatedTasDashboardV3Route extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare router: RouterService;
  @service declare features: FeaturesService;

  // In production, redirect to the original dashboard
  beforeModel() {
    if (this.features.isDisabled('newTasDashboard')) {
      this.router.transitionTo('authenticated.tas.dashboard');
    }
  }

  async model() {
    const currentEmployee = this.sessionContext.currentEmployee;

    // Validate employee exists and has required relationships
    if (!currentEmployee?.id) {
      console.error('No current employee found in session context');
      throw new Error('Employee information not loaded. Please refresh the page.');
    }

    const companyId = currentEmployee.company?.id;
    if (!companyId) {
      console.error('Company ID not available on currentEmployee', {
        employeeId: currentEmployee.id,
        company: currentEmployee.company,
      });
      throw new Error('Company information not loaded. Please refresh the page.');
    }

    let templates = await this.store.query<TASProgramTemplateModel>('tas-program-template', {
      filter: {
        state: 'TAS.ProgramTemplateState.LAUNCHED',
        byEligibility: currentEmployee.id,
        company: companyId,
      },
      include: 'company.company-setting',
      reload: true,
      page: {
        limit: 100,
      },
    });

    const promises = [
      // Load the TasParticipant fresh to ensure we alert any HELD participant state
      this.store.findRecord('employee', currentEmployee.id, {
        include: 'tas-participant,company',
        reload: true,
      }),
      this.tuitionAssistance.getV2LimitsStatus(currentEmployee, undefined, false),
    ];

    const currentUserCanViewTrialTemplates =
      this.sessionContext.user.isEmployeeAdmin || this.sessionContext.user.isAccountOwner;

    if (currentUserCanViewTrialTemplates) {
      promises.push(
        this.store.query<TASProgramTemplateModel>('tas-program-template', {
          filter: {
            state: 'TAS.ProgramTemplateState.LAUNCHED',
            isTrial: true,
            company: companyId,
          },
          include: 'company.company-setting',
        })
      );
    }

    const [employeeWithParticipants, limitsStatus, trialTemplates = []] = (await Promise.all(
      promises
    )) as [EmployeeModel, LimitsStatusDataSignature | undefined, TASProgramTemplateModel[]];

    // tasParticipant may be null if employee isn't enrolled in TAS yet

    const tasParticipant = employeeWithParticipants.tasParticipant;
    const tasParticipantId = tasParticipant?.id;

    // If no TAS participant, we can still show templates but skip eligibility checks
    let dynamicEligibility = {};
    let programEligibility = {};

    if (tasParticipantId) {
      [dynamicEligibility, programEligibility] = await Promise.all([
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        this.store.adapterFor('tas-participant').dynamicEligibility(tasParticipantId),
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        this.store.adapterFor('tas-participant').periodOfEligibility(tasParticipantId),
      ]);
    }

    if (currentUserCanViewTrialTemplates) {
      // @ts-expect-error not sure
      templates = [...templates, ...trialTemplates];
    }

    const templatesFilteredByEligibility = filterEligibleTemplates(
      templates,
      dynamicEligibility,
      programEligibility
    );

    const isEmployeeProgram = (program: TASProgramTemplateModel) => {
      return program.isDependentProgram === false;
    };

    const filteredTemplates = templatesFilteredByEligibility.filter(isEmployeeProgram);

    // Fetch recent applications and separate Type 4 from flat types
    const { flatApplications, programInstances } = await this.fetchRecentApplications(
      currentEmployee.id,
      companyId
    );

    // Extract unique template IDs that the employee is enrolled in (has active applications)
    const enrolledTemplateIds = new Set(
      [...flatApplications, ...programInstances.flatMap((inst) => inst.childApplications)]
        .filter((app) => app.tasProgramTemplateIdKeyword)
        .map((app) => app.tasProgramTemplateIdKeyword as string)
    );

    return {
      templates: filteredTemplates,
      recentApplications: flatApplications,
      programInstances,
      enrolledTemplateIds,
      isHeldParticipant: !!employeeWithParticipants.tasParticipant?.isHeld,
      limitsStatus,
    };
  }

  /**
   * Fetch recent applications via OpenSearch with smart filtering rules:
   *
   * Type 1: Show if active, hide if inactive
   * Type 2/3: Show if active, show inactive only within 1 month
   * Type 4.2/4.3:
   *   - Active instance: show all active apps, or 1 most recent if no active apps
   *   - Inactive instance: show all active apps, or apps within 1 month, or empty
   *
   * OVERRIDE: Apps with paymentRefundRequestedAmount > 0 are ALWAYS shown
   */
  async fetchRecentApplications(
    employeeId: string,
    companyId: string
  ): Promise<{
    flatApplications: RecentApplication[];
    programInstances: ProgramInstance[];
  }> {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    try {
      // Fetch all data without time filter - we'll filter in post-processing
      // This allows us to apply complex conditional logic per type
      const builder = new OpenSearchQueryBuilder()
        .mustTerm('company_id', companyId)
        .mustTerm('employee_id', employeeId)
        .addFilter({
          bool: {
            should: [
              { terms: { tas_application_state_keyword: ALL_APPLICATION_STATES } },
              { terms: { tas_program_instance_state_keyword: ALL_PROGRAM_INSTANCE_STATES } },
            ],
            minimum_should_match: 1,
          },
        })
        // Use a broader time filter (1 month) for the query, then filter further in JS
        // This ensures we get enough data for the conditional logic while limiting total results
        .addFilter({
          bool: {
            should: [
              // Active states - no time limit
              { terms: { tas_application_state_keyword: ACTIVE_APPLICATION_STATES } },
              { terms: { tas_program_instance_state_keyword: ACTIVE_INSTANCE_STATES } },
              // Inactive states - within time window
              { range: { updated_at: { gte: oneMonthAgo.toISOString() } } },
            ],
            minimum_should_match: 1,
          },
        })
        .setSort('updated_at:desc')
        .setPagination(1, 200);

      const query = {
        'model-type': 'TasProgramInstance',
        searchBody: builder.build(),
      };

      const { content: response } = (await this.store.request(executeSearchRequest(query))) as {
        content: OpenSearchResponse<RecentApplication>;
      };
      const responseBuilder = new OpenSearchResponseBuilder<RecentApplication>(response);
      const rawDocs = responseBuilder.getDocuments();
      const allApps = normalizePaymentFields(rawDocs);

      // Apply filtering rules and separate by type
      return this.applyFilteringRules(allApps, oneMonthAgo);
    } catch (error) {
      console.error('Failed to fetch recent applications:', error);
      return { flatApplications: [], programInstances: [] };
    }
  }

  /**
   * Apply the filtering rules based on type and state
   */
  private applyFilteringRules(
    allApps: RecentApplication[],
    oneMonthAgo: Date
  ): {
    flatApplications: RecentApplication[];
    programInstances: ProgramInstance[];
  } {
    const flatApps: RecentApplication[] = [];
    const type4InstanceMap = new Map<
      string,
      { instance: RecentApplication | null; children: RecentApplication[] }
    >();
    // Type 1 instances are stored separately (they have no children)
    const type1Instances: ProgramInstance[] = [];

    for (const app of allApps) {
      const type = app.tasProgramTemplateTypeKeyword;
      const isInstanceRecord = app.recordType === 'instance';
      const appIsActive = isActiveApplicationState(app.tasApplicationStateKeyword);
      const instanceIsActive = isActiveInstanceState(app.tasProgramInstanceStateKeyword);
      const appInRepayment = isInRepayment(app);
      const appUpdatedAt = new Date(app.updatedAt || 0);
      const isWithin1Month = appUpdatedAt >= oneMonthAgo;

      // Type 1: Program instance with no child applications
      // Uses tasProgramInstanceStateKeyword, not tasApplicationStateKeyword
      // Rendered as a ProgramInstance (not flat application)
      if (type === 'TAS.ProgramType.1') {
        if (instanceIsActive || appInRepayment) {
          type1Instances.push({
            instanceApplication: app,
            childApplications: [],
          });
        }
        // Inactive Type 1 instances are NOT shown (unless in repayment)
        continue;
      }

      // Type 2 & 3: Show if active, or inactive within 1 month
      if (type === 'TAS.ProgramType.2' || type === 'TAS.ProgramType.3') {
        if (appIsActive || appInRepayment || isWithin1Month) {
          flatApps.push(app);
        }
        continue;
      }

      // Type 4.2 & 4.3: Group by instance for later processing
      if (type === 'TAS.ProgramType.4.2' || type === 'TAS.ProgramType.4.3') {
        const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;

        if (!type4InstanceMap.has(instanceId)) {
          type4InstanceMap.set(instanceId, { instance: null, children: [] });
        }

        const group = type4InstanceMap.get(instanceId)!;
        if (isInstanceRecord) {
          group.instance = app;
        } else {
          group.children.push(app);
        }
      }
    }

    // Process Type 4.2/4.3 instances with their children
    const type4Instances = this.filterType42And43Instances(type4InstanceMap, oneMonthAgo);

    // Combine Type 1 instances with Type 4.2/4.3 instances
    // Both are rendered as ProgramInstanceCard (Type 1 has no children, so not expandable)
    const programInstances = [...type1Instances, ...type4Instances];

    return { flatApplications: flatApps, programInstances };
  }

  /**
   * Filter Type 4.2/4.3 instances and their child applications
   */
  private filterType42And43Instances(
    instanceMap: Map<string, { instance: RecentApplication | null; children: RecentApplication[] }>,
    oneMonthAgo: Date
  ): ProgramInstance[] {
    const result: ProgramInstance[] = [];

    for (const [, { instance, children }] of instanceMap) {
      // Sort children by updatedAt descending for "most recent" logic
      const sortedChildren = [...children].sort(
        (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
      );

      // Some OS results may omit the instance record; fall back to the most recent child.
      const instanceRecord = instance ?? sortedChildren[0];
      if (!instanceRecord) {
        continue;
      }

      const instanceIsActive = isActiveInstanceState(instanceRecord.tasProgramInstanceStateKeyword);
      const instanceInRepayment = isInRepayment(instanceRecord);

      // Find active children and children in repayment
      const activeChildren = children.filter(
        (c) => isActiveApplicationState(c.tasApplicationStateKeyword) || isInRepayment(c)
      );

      let filteredChildren: RecentApplication[] = [];

      if (activeChildren.length > 0) {
        // Has active children → show all active children (same for both active and inactive instances)
        filteredChildren = activeChildren;
      } else if (instanceIsActive || instanceInRepayment) {
        // Active instance with no active children → show 1 most recent child
        if (sortedChildren.length > 0) {
          filteredChildren = [sortedChildren[0]!];
        }
      } else {
        // Inactive instance with no active children → show children within 1 month
        filteredChildren = children.filter((c) => {
          const childUpdatedAt = new Date(c.updatedAt || 0);
          return childUpdatedAt >= oneMonthAgo || isInRepayment(c);
        });
      }

      // Always include instance if it matches criteria (has filtered children, is active, or in repayment)
      // Even empty instances should show if the instance itself is active or has recent activity
      const instanceUpdatedAt = new Date(instanceRecord.updatedAt || 0);
      const instanceWithin1Month = instanceUpdatedAt >= oneMonthAgo;

      if (
        filteredChildren.length > 0 ||
        instanceIsActive ||
        instanceInRepayment ||
        instanceWithin1Month
      ) {
        result.push({
          instanceApplication: instanceRecord,
          childApplications: filteredChildren,
        });
      }
    }

    // Sort instances by most recent activity (instance or children)
    return result.sort((a, b) => {
      const aDate = this.getMostRecentDate(a);
      const bDate = this.getMostRecentDate(b);
      return bDate - aDate;
    });
  }

  /**
   * Get the most recent date from an instance (instance itself or its children)
   */
  private getMostRecentDate(instance: ProgramInstance): number {
    const instanceDate = new Date(instance.instanceApplication.updatedAt || 0).getTime();
    const childDates = instance.childApplications.map((c) => new Date(c.updatedAt || 0).getTime());

    if (childDates.length === 0) {
      return instanceDate;
    }

    return Math.max(instanceDate, ...childDates);
  }
}
